/*
 * PKI.Pem — PEM/DER container handling and input sniffing.
 *
 * The sniffing half matters more than the encoding half. Every competing online
 * decoder demands a clean "-----BEGIN CERTIFICATE-----" block and shows
 * "invalid input" for anything else, but almost nobody has one on the clipboard:
 * they have a Kubernetes secret (base64 OF a PEM), a Terraform/JSON string with
 * literal \n, an indented YAML block, an `openssl x509 -text` dump with the PEM
 * at the bottom, or a raw .der/.cer binary file. All of those are unambiguous
 * once you look — so we look, and say what we found.
 */
(function (root) {
    'use strict';

    var Asn1 = (root && root.PKI && root.PKI.Asn1) ||
        (typeof require !== 'undefined' ? require('./asn1.js') : null);

    var B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

    /* ─── base64 (browser + Node, no dependency on atob availability) ── */

    function toBase64(bytes) {
        var b = Asn1.u8(bytes), out = '', i;
        for (i = 0; i + 2 < b.length; i += 3) {
            var n = (b[i] << 16) | (b[i + 1] << 8) | b[i + 2];
            out += B64_CHARS[(n >> 18) & 63] + B64_CHARS[(n >> 12) & 63] +
                   B64_CHARS[(n >> 6) & 63] + B64_CHARS[n & 63];
        }
        var rem = b.length - i;
        if (rem === 1) {
            var n1 = b[i] << 16;
            out += B64_CHARS[(n1 >> 18) & 63] + B64_CHARS[(n1 >> 12) & 63] + '==';
        } else if (rem === 2) {
            var n2 = (b[i] << 16) | (b[i + 1] << 8);
            out += B64_CHARS[(n2 >> 18) & 63] + B64_CHARS[(n2 >> 12) & 63] +
                   B64_CHARS[(n2 >> 6) & 63] + '=';
        }
        return out;
    }

    function fromBase64(str) {
        // Accept standard and URL-safe alphabets, ignore all whitespace.
        var s = String(str).replace(/[\s\r\n]+/g, '').replace(/-/g, '+').replace(/_/g, '/');
        s = s.replace(/=+$/, '');
        var out = new Uint8Array(Math.floor(s.length * 3 / 4)), o = 0, buf = 0, bits = 0;
        for (var i = 0; i < s.length; i++) {
            var v = B64_CHARS.indexOf(s[i]);
            if (v < 0) throw new Error('invalid base64 character "' + s[i] + '" at ' + i);
            buf = (buf << 6) | v;
            bits += 6;
            if (bits >= 8) { bits -= 8; out[o++] = (buf >> bits) & 0xff; }
        }
        return out.subarray(0, o);
    }

    /* ─── encode ────────────────────────────────────────────────────── */

    function encode(der, label) {
        var b64 = toBase64(der), lines = [];
        for (var i = 0; i < b64.length; i += 64) lines.push(b64.substr(i, 64));
        var tag = String(label || 'CERTIFICATE').toUpperCase();
        return '-----BEGIN ' + tag + '-----\n' + lines.join('\n') + '\n-----END ' + tag + '-----\n';
    }

    /** Join several DER blobs into one multi-block PEM (a chain file). */
    function encodeAll(items) {
        return items.map(function (it) { return encode(it.der, it.label); }).join('');
    }

    /* ─── normalisation ─────────────────────────────────────────────── */

    /**
     * Undo the four ways a PEM arrives mangled. Order matters: JSON escapes
     * must be resolved before we look for line structure, and indentation must
     * go before base64 extraction.
     */
    function normalizeText(text) {
        var s = String(text);
        // 1. JSON / Terraform / .env single-line form with literal backslash-n.
        if (s.indexOf('\\n') >= 0 && /-{5}BEGIN/.test(s)) {
            s = s.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\r/g, '\n');
        }
        // 2. Surrounding quotes from a JSON/YAML scalar.
        s = s.replace(/^\s*["']|["']\s*$/g, '');
        // 3. CRLF and lone CR.
        s = s.replace(/\r\n?/g, '\n');
        // 4. YAML block indentation on every line.
        if (/^\s+-{5}BEGIN/m.test(s)) s = s.replace(/^[ \t]+/gm, '');
        return s;
    }

    var BLOCK_RE = /-{5}BEGIN ([A-Z0-9 ._-]+?)-{5}([\s\S]*?)-{5}END \1-{5}/g;
    // Deliberately tolerant: some tools emit 4 or 6 dashes, or omit the label
    // on END. Handled by a second, looser pass when the strict one finds nothing.
    var LOOSE_RE = /-{3,}\s*BEGIN ([A-Z0-9 ._-]+?)\s*-{3,}([\s\S]*?)-{3,}\s*END[^-]*-{3,}/g;

    /**
     * Extract every PEM block. Returns [{label, der, headers, raw}].
     * Blocks that fail base64 or ASN.1 are still returned, with `.error` set —
     * a partially corrupt chain file should show the good certs, not nothing.
     */
    function decode(text) {
        var s = normalizeText(text), out = [], m;

        BLOCK_RE.lastIndex = 0;
        while ((m = BLOCK_RE.exec(s)) !== null) out.push(buildBlock(m[1], m[2], m[0]));
        if (!out.length) {
            LOOSE_RE.lastIndex = 0;
            while ((m = LOOSE_RE.exec(s)) !== null) out.push(buildBlock(m[1], m[2], m[0]));
        }
        return out;
    }

    function buildBlock(label, body, raw) {
        var block = { label: label.trim().toUpperCase(), headers: {}, der: null, error: null, raw: raw };
        var text = body;

        // RFC 1421 headers ("Proc-Type: 4,ENCRYPTED" / "DEK-Info: AES-256-CBC,…")
        // mark a legacy OpenSSL encrypted key — the tools must detect and explain
        // these rather than hand back garbage.
        var headerMatch = /^\s*((?:[A-Za-z-]+:[^\n]*\n)+)\s*\n/.exec(text);
        if (headerMatch) {
            headerMatch[1].split('\n').forEach(function (line) {
                var idx = line.indexOf(':');
                if (idx > 0) block.headers[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
            });
            text = text.slice(headerMatch[0].length);
        }

        try {
            block.der = fromBase64(text);
            if (!block.der.length) block.error = 'empty';
        } catch (e) {
            block.error = e.message;
        }
        return block;
    }

    /* ─── sniffing ──────────────────────────────────────────────────── */

    var LABEL_KIND = {
        'CERTIFICATE': 'certificate',
        'X509 CERTIFICATE': 'certificate',
        'TRUSTED CERTIFICATE': 'certificate',
        'CERTIFICATE REQUEST': 'csr',
        'NEW CERTIFICATE REQUEST': 'csr',
        'PRIVATE KEY': 'privateKey',
        'RSA PRIVATE KEY': 'privateKey',
        'EC PRIVATE KEY': 'privateKey',
        'DSA PRIVATE KEY': 'privateKey',
        'ENCRYPTED PRIVATE KEY': 'encryptedPrivateKey',
        'PUBLIC KEY': 'publicKey',
        'RSA PUBLIC KEY': 'publicKey',
        'X509 CRL': 'crl',
        'PKCS7': 'pkcs7',
        'CMS': 'pkcs7',
        'EC PARAMETERS': 'ecParameters',
        'DH PARAMETERS': 'dhParameters'
    };

    /**
     * Work out what the user gave us. Returns
     *   { kind, source, blocks:[{label, kind, der}], notes:[] }
     * `source` records HOW it was recognised so the UI can tell the user
     * ("this was a base64-wrapped PEM — a Kubernetes secret, probably").
     */
    function sniff(input) {
        var notes = [], blocks = [];

        // Binary input (a File read as ArrayBuffer) — try DER directly.
        if (input instanceof Uint8Array || input instanceof ArrayBuffer) {
            var bytes = Asn1.u8(input);
            if (looksLikeText(bytes)) {
                return sniff(new TextDecoder('utf-8').decode(bytes));
            }
            return { kind: derKind(bytes), source: 'der', blocks: [{ label: null, kind: derKind(bytes), der: bytes }], notes: notes };
        }

        var text = String(input || '').trim();
        if (!text) return { kind: 'empty', source: null, blocks: [], notes: notes };

        // 1. Straight PEM (after normalisation).
        var pemBlocks = decode(text);
        if (pemBlocks.length) {
            if (/\\n/.test(text)) notes.push('escaped-newlines');
            if (/^\s+-{5}BEGIN/m.test(text)) notes.push('indented');
            blocks = pemBlocks.map(function (b) {
                return { label: b.label, kind: LABEL_KIND[b.label] || derKind(b.der), der: b.der, headers: b.headers, error: b.error };
            });
            return { kind: blocks[0].kind, source: 'pem', blocks: blocks, notes: notes };
        }

        // 2. Hex dump (`openssl … -outform DER | xxd`, or a copied hex string).
        var hexish = text.replace(/[\s:,-]/g, '');
        if (/^[0-9a-fA-F]+$/.test(hexish) && hexish.length >= 32 && hexish.length % 2 === 0) {
            var hexBytes = Asn1.fromHex(hexish);
            if (isDer(hexBytes)) {
                notes.push('hex');
                return { kind: derKind(hexBytes), source: 'hex', blocks: [{ label: null, kind: derKind(hexBytes), der: hexBytes }], notes: notes };
            }
        }

        // 3. Bare base64 — either of DER, or of a whole PEM file. The second
        //    case is what `kubectl get secret -o yaml` produces and is the single
        //    most common "why does your tool say invalid" input.
        var compact = text.replace(/\s+/g, '');
        if (/^[A-Za-z0-9+/=_-]+$/.test(compact) && compact.length >= 32) {
            var decoded = null;
            try { decoded = fromBase64(compact); } catch (e) { decoded = null; }
            if (decoded && decoded.length) {
                if (looksLikeText(decoded)) {
                    var inner = new TextDecoder('utf-8').decode(decoded);
                    if (/-{5}BEGIN/.test(inner)) {
                        var res = sniff(inner);
                        res.notes = res.notes.concat(['base64-wrapped-pem']);
                        res.source = 'base64-pem';
                        return res;
                    }
                }
                if (isDer(decoded)) {
                    notes.push('bare-base64');
                    return { kind: derKind(decoded), source: 'base64', blocks: [{ label: null, kind: derKind(decoded), der: decoded }], notes: notes };
                }
            }
        }

        return { kind: 'unknown', source: null, blocks: [], notes: notes };
    }

    /** Cheap heuristic: is this buffer text rather than binary DER? */
    function looksLikeText(bytes) {
        var n = Math.min(bytes.length, 256), printable = 0;
        for (var i = 0; i < n; i++) {
            var c = bytes[i];
            if (c === 9 || c === 10 || c === 13 || (c >= 32 && c < 127)) printable++;
        }
        return n > 0 && printable / n > 0.95;
    }

    function isDer(bytes) {
        if (!bytes || bytes.length < 4) return false;
        if (bytes[0] !== 0x30) return false; // every structure we care about is a SEQUENCE
        try {
            var node = Asn1.parse(bytes);
            return node.end === bytes.length || !!node.trailingBytes;
        } catch (e) { return false; }
    }

    /**
     * Identify a DER blob by shape when there is no PEM label to trust.
     * Deliberately structural, not guesswork: a Certificate's TBS starts with
     * either [0] version or the serial INTEGER, a CSR's first element is an
     * INTEGER version 0 followed by a SEQUENCE subject, PKCS#12 starts with
     * INTEGER 3, PKCS#8 with INTEGER 0 then an AlgorithmIdentifier.
     */
    function derKind(bytes) {
        var node;
        try { node = Asn1.parse(bytes); } catch (e) { return 'unknown'; }
        if (node.tag !== Asn1.TAG.SEQUENCE || !node.children) return 'unknown';
        var kids = node.children;

        // PKCS#12 PFX: SEQUENCE { INTEGER 3, ContentInfo, MacData }
        if (kids.length >= 2 && kids[0].tag === Asn1.TAG.INTEGER &&
            kids[0].bytes.length === 1 && kids[0].bytes[0] === 3 &&
            kids[1].tag === Asn1.TAG.SEQUENCE && kids[1].children &&
            kids[1].children[0] && kids[1].children[0].oid === '1.2.840.113549.1.7.1') {
            return 'pkcs12';
        }

        // Certificate / CSR / CRL all start SEQUENCE { SEQUENCE tbs, SEQUENCE algid, BIT STRING sig }
        if (kids.length === 3 && kids[0].tag === Asn1.TAG.SEQUENCE &&
            kids[1].tag === Asn1.TAG.SEQUENCE && kids[2].tag === Asn1.TAG.BIT_STRING) {
            var tbs = kids[0].children || [];
            // CSR: CertificationRequestInfo { INTEGER version, Name subject, SPKI }
            if (tbs.length >= 3 && tbs[0].tag === Asn1.TAG.INTEGER &&
                tbs[1].tag === Asn1.TAG.SEQUENCE && tbs[2].tag === Asn1.TAG.SEQUENCE &&
                tbs[2].children && tbs[2].children[0] && tbs[2].children[0].tag === Asn1.TAG.SEQUENCE) {
                return 'csr';
            }
            // CRL: TBSCertList { [version], AlgorithmIdentifier, Name issuer, Time thisUpdate }
            if (tbs.length >= 3 && tbs[0].tag !== 0xa0 &&
                tbs[0].tag === Asn1.TAG.INTEGER && tbs[1].tag === Asn1.TAG.SEQUENCE &&
                tbs[2].tag === Asn1.TAG.SEQUENCE && tbs[3] &&
                (tbs[3].tag === Asn1.TAG.UTC_TIME || tbs[3].tag === Asn1.TAG.GENERALIZED_TIME)) {
                return 'crl';
            }
            return 'certificate';
        }

        // PKCS#8 PrivateKeyInfo: SEQUENCE { INTEGER 0, AlgorithmIdentifier, OCTET STRING }
        if (kids.length >= 3 && kids[0].tag === Asn1.TAG.INTEGER &&
            kids[1].tag === Asn1.TAG.SEQUENCE && kids[2].tag === Asn1.TAG.OCTET_STRING) {
            return 'privateKey';
        }
        // EncryptedPrivateKeyInfo: SEQUENCE { AlgorithmIdentifier, OCTET STRING }
        if (kids.length === 2 && kids[0].tag === Asn1.TAG.SEQUENCE &&
            kids[1].tag === Asn1.TAG.OCTET_STRING) {
            return 'encryptedPrivateKey';
        }
        // SPKI: SEQUENCE { AlgorithmIdentifier, BIT STRING }
        if (kids.length === 2 && kids[0].tag === Asn1.TAG.SEQUENCE &&
            kids[1].tag === Asn1.TAG.BIT_STRING) {
            return 'publicKey';
        }
        // PKCS#1 RSAPrivateKey: 9 INTEGERs. RSAPublicKey: 2 INTEGERs.
        if (kids.length >= 9 && kids.every(function (k) { return k.tag === Asn1.TAG.INTEGER; })) {
            return 'privateKey';
        }
        if (kids.length === 2 && kids[0].tag === Asn1.TAG.INTEGER && kids[1].tag === Asn1.TAG.INTEGER) {
            return 'publicKey';
        }
        // SEC1 ECPrivateKey: SEQUENCE { INTEGER 1, OCTET STRING, [0] curve, [1] pub }
        if (kids.length >= 2 && kids[0].tag === Asn1.TAG.INTEGER &&
            kids[0].bytes.length === 1 && kids[0].bytes[0] === 1 &&
            kids[1].tag === Asn1.TAG.OCTET_STRING) {
            return 'privateKey';
        }
        // PKCS#7 / CMS ContentInfo
        if (kids.length >= 1 && kids[0].tag === Asn1.TAG.OID &&
            String(kids[0].oid || '').indexOf('1.2.840.113549.1.7.') === 0) {
            return 'pkcs7';
        }
        return 'unknown';
    }

    /** Canonical PEM label for a detected kind (used when re-emitting). */
    function labelFor(kind) {
        switch (kind) {
            case 'certificate': return 'CERTIFICATE';
            case 'csr': return 'CERTIFICATE REQUEST';
            case 'privateKey': return 'PRIVATE KEY';
            case 'encryptedPrivateKey': return 'ENCRYPTED PRIVATE KEY';
            case 'publicKey': return 'PUBLIC KEY';
            case 'crl': return 'X509 CRL';
            case 'pkcs7': return 'PKCS7';
            default: return 'CERTIFICATE';
        }
    }

    var API = {
        toBase64: toBase64, fromBase64: fromBase64,
        encode: encode, encodeAll: encodeAll, decode: decode,
        normalizeText: normalizeText, sniff: sniff, derKind: derKind,
        isDer: isDer, labelFor: labelFor, LABEL_KIND: LABEL_KIND
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Pem = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
